import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  BookOpen, 
  FileText, 
  Users, 
  Globe, 
  Award, 
  PenTool, 
  Layers, 
  CheckCircle, 
  ArrowRight, 
  Target,
  BookMarked,
  GraduationCap,
  Briefcase,
  Shield
} from "lucide-react";

export default function Services() {
  return (
    <div data-testid="services-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-[1px] w-12 bg-[#C5A059]" />
              <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">What We Offer</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl font-medium text-white mb-6">
              Our Services
            </h1>
            <p className="text-slate-400 text-lg">
              Comprehensive publishing solutions tailored for academics, researchers, and institutions worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-slate-900 mb-6">Publishing Services</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto mb-6" />
            <p className="text-slate-600 max-w-2xl mx-auto">
              From manuscript to market, we provide end-to-end publishing solutions with a commitment to quality and academic excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ServiceCard 
              icon={BookOpen}
              title="Book Publishing"
              description="Complete publication of authored books, research monographs, and scholarly treatises across all academic disciplines."
              features={["ISBN Assignment", "Professional Cover Design", "Quality Print Production", "Digital & Print Formats"]}
            />
            <ServiceCard 
              icon={Layers}
              title="Edited Volumes"
              description="Publication of multi-author edited volumes, conference proceedings, and collaborative scholarly works."
              features={["Chapter Coordination", "Peer Review Management", "Indexing Services", "Cross-referencing"]}
            />
            <ServiceCard 
              icon={GraduationCap}
              title="Textbook Publishing"
              description="Comprehensive textbook development for universities and academic institutions at all levels."
              features={["Curriculum Alignment", "Interactive Elements", "Study Materials", "Instructor Resources"]}
            />
            <ServiceCard 
              icon={Globe}
              title="International Publishing"
              description="Global reach through our international publishing divisions - Blue Globe and Aquitas International."
              features={["Global Distribution", "Multi-language Support", "International ISBN", "Worldwide Visibility"]}
            />
            <ServiceCard 
              icon={PenTool}
              title="Editorial Services"
              description="Professional editing, proofreading, and manuscript preparation services for academic excellence."
              features={["Copy Editing", "Developmental Editing", "Proofreading", "Formatting & Styling"]}
            />
            <ServiceCard 
              icon={Target}
              title="Marketing & Distribution"
              description="Strategic marketing and distribution to ensure your work reaches the right academic audience."
              features={["Online Catalogues", "Academic Networks", "Library Distribution", "Conference Exposure"]}
            />
          </div>
        </div>
      </section>

      {/* Academic Support Section */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-white mb-6">Academic Support Services</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto mb-6" />
            <p className="text-slate-400 max-w-2xl mx-auto">
              Beyond publishing, we offer specialized support services for academics and institutions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <SupportCard 
              icon={Users}
              title="Peer Review Panel"
              description="Join our expert review panel and contribute to academic excellence."
              link="/review-panel"
              linkText="Join Panel"
            />
            <SupportCard 
              icon={Award}
              title="ISBN & Indexing"
              description="ISBN assignment, cataloging, and indexing in major academic databases."
              link="/contact"
              linkText="Learn More"
            />
            <SupportCard 
              icon={Briefcase}
              title="Institutional Partnerships"
              description="Collaborative publishing programs for universities and research institutions."
              link="/contact"
              linkText="Partner With Us"
            />
            <SupportCard 
              icon={Shield}
              title="Copyright & IP"
              description="Guidance on copyright registration, intellectual property, and legal compliance."
              link="/contact"
              linkText="Get Guidance"
            />
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-slate-900 mb-6">Our Publishing Process</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto" />
          </div>

          <div className="grid md:grid-cols-5 gap-4">
            <ProcessStep number="01" title="Submission" description="Submit your manuscript via email" />
            <ProcessStep number="02" title="Review" description="Peer review and editorial assessment" />
            <ProcessStep number="03" title="Editing" description="Professional editing and formatting" />
            <ProcessStep number="04" title="Design" description="Cover design and layout finalization" />
            <ProcessStep number="05" title="Publication" description="Print, distribute, and market" />
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-[1px] w-12 bg-[#C5A059]" />
                <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Why Choose Us</span>
              </div>
              <h2 className="font-serif text-4xl font-medium text-slate-900 mb-6">
                Trusted by Academics Nationwide
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                SPA Publication Group has established itself as a trusted name in academic publishing. With five specialized publishing houses under one umbrella, we offer unparalleled expertise and reach for your scholarly work.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">Rigorous peer review process ensuring quality</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">International reach through global publishing divisions</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">Professional editorial and design team</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">Quick turnaround time with transparent process</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">Affordable publishing options for all authors</span>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-950 p-8 text-center">
                <div className="font-serif text-5xl text-[#C5A059] font-bold mb-2">5</div>
                <p className="text-white text-sm">Publishing Houses</p>
              </div>
              <div className="bg-[#C5A059] p-8 text-center">
                <div className="font-serif text-5xl text-slate-950 font-bold mb-2">50+</div>
                <p className="text-slate-950 text-sm">Publications</p>
              </div>
              <div className="bg-[#C5A059] p-8 text-center">
                <div className="font-serif text-5xl text-slate-950 font-bold mb-2">100+</div>
                <p className="text-slate-950 text-sm">Authors</p>
              </div>
              <div className="bg-slate-950 p-8 text-center">
                <div className="font-serif text-5xl text-[#C5A059] font-bold mb-2">2</div>
                <p className="text-white text-sm">International Divisions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#C5A059]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-serif text-2xl font-medium text-slate-950 mb-2">Ready to Publish Your Work?</h3>
              <p className="text-slate-800">Submit your manuscript today and let us bring your academic vision to life.</p>
            </div>
            <div className="flex gap-4">
              <Button asChild className="bg-slate-950 hover:bg-slate-800 text-white rounded-none px-8 py-6">
                <Link to="/submission">Submit Manuscript <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Button asChild variant="outline" className="border-slate-950 text-slate-950 hover:bg-slate-950 hover:text-white rounded-none px-8 py-6">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ icon: Icon, title, description, features }) {
  return (
    <Card className="bg-white border border-slate-100 rounded-none hover:shadow-xl transition-all group" data-testid={`service-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-8">
        <div className="w-16 h-16 bg-slate-950 flex items-center justify-center mb-6 group-hover:bg-[#C5A059] transition-colors">
          <Icon className="w-8 h-8 text-[#C5A059] group-hover:text-slate-950 transition-colors" />
        </div>
        <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">{title}</h3>
        <p className="text-slate-600 text-sm mb-6 leading-relaxed">{description}</p>
        <ul className="space-y-2">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2 text-slate-500 text-sm">
              <CheckCircle className="w-4 h-4 text-[#C5A059]" />
              {feature}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function SupportCard({ icon: Icon, title, description, link, linkText }) {
  return (
    <div className="bg-slate-900 p-6 border border-slate-800 hover:border-[#C5A059]/30 transition-colors" data-testid={`support-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-12 h-12 bg-[#C5A059]/10 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-[#C5A059]" />
      </div>
      <h3 className="font-serif text-lg font-medium text-white mb-2">{title}</h3>
      <p className="text-slate-400 text-sm mb-4">{description}</p>
      <Link to={link} className="text-[#C5A059] text-sm font-medium hover:underline inline-flex items-center gap-1">
        {linkText} <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

function ProcessStep({ number, title, description }) {
  return (
    <div className="text-center relative">
      <div className="w-16 h-16 bg-slate-950 flex items-center justify-center mx-auto mb-4">
        <span className="font-serif text-xl font-bold text-[#C5A059]">{number}</span>
      </div>
      <h3 className="font-serif text-lg font-medium text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-500 text-sm">{description}</p>
    </div>
  );
}
